import React from 'react';

import styles from '~/components/entity-search/entity-search-main-details/entity-search-main-details.module.scss';

import { EntitySearchType } from '~/components/entity-search/entity-type';
import { EntityTypeUtils } from '~/components/entity-search/entity-type-util';
import { EntityType } from '~/models/entities/entity-type';

interface Props {
	// title: string;
	// list: string[] | undefined;
	entity: EntitySearchType;
	size?: 'small';
}

export class EntitySearchMainDetails extends React.Component<Props> {
	constructor(props) {
		super(props);
	}

	/**
	 * getPrimaryNameInfo will return name information that must always be shown
	 * on-screen.
	 */
	private getPrimaryNameInfo() {
		const { entity } = this.props;
		if (!entity || !entity.entityType) {
			return '';
		}
		switch (entity.entityType) {
			case EntityType.MEMBER: {
				const record = entity.values;

				let displayName = '';
				if (record.D2z_Title) {
					displayName += record.D2z_Title.trim();
				}
				let surname = '';
				if (record.MDz_Surname) {
					surname = record.MDz_Surname;
				} else if (record.D2z_Surname) {
					surname = record.D2z_Surname;
				}
				displayName += ' ' + surname;
				return displayName.trim();
			}

			case EntityType.EMPLOYER: {
				if (entity.values.COz_Payroll_Name) {
					return entity.values.COz_Payroll_Name.trim();
				}
				break;
			}

			case EntityType.INVESTMENT_MANAGER: {
				return entity.values.IMz_Name;
			}

			case EntityType.ADVISER: {
				if (entity.values.BDz_Business_Name) {
					return entity.values.BDz_Business_Name;
				}
				return entity.values.BDz_Name;
			}

			default:
				const exhaustiveCheck: never = entity;
				throw new Error(
					'Unhandled entity type: ' + (entity as any).entityType,
				);
		}
		return '';
	}

	/**
	 * getSecondaryNameInfo will return additional name information that we're willing to
	 * omit for smaller screens.
	 */
	private getSecondaryNameInfo() {
		const { entity } = this.props;
		if (!entity || !entity.entityType) {
			return '';
		}
		switch (entity.entityType) {
			case EntityType.MEMBER: {
				const record = entity.values;
				let givenName = '';
				if (record.MDz_Given_Names) {
					givenName = this.mergeSecondaryName(
						record.MDz_Given_Names,
						record.MDz_Middle_Names,
					);
				} else if (record.D2z_Given_Names) {
					givenName = this.mergeSecondaryName(
						record.D2z_Given_Names,
						record.D2z_Middle_Names,
					);
				}
				return givenName.trim();
			}

			case EntityType.EMPLOYER:
			case EntityType.INVESTMENT_MANAGER:
			case EntityType.ADVISER:
				return '';

			default:
				const exhaustiveCheck: never = entity;
				throw new Error(
					'Unhandled entity type: ' + (entity as any).entityType,
				);
		}
		return '';
	}

	public render() {
		const { entity, children, size } = this.props;

		const primaryName = this.getPrimaryNameInfo();
		const secondaryName = this.getSecondaryNameInfo();
		const title = primaryName + ', ' + secondaryName;
		const list = EntityTypeUtils.getMainDetails(entity);

		return (
			<div className={styles.root}>
				<p
					title={title}
					className={[
						styles.title,
						size === 'small' ? styles['title--small'] : '',
					].join(' ')}
				>
					<span>{primaryName}</span>
					<span className={styles['title-secondary']}>
						, {secondaryName}
					</span>
				</p>
				{list && list.length > 0 && (
					<ul className={styles.list}>
						{list.map((text, i) => {
							return (
								<li key={text} className={styles.item}>
									<span
										className={[
											styles.text,
											size === 'small'
												? styles['text--small']
												: '',
										].join(' ')}
									>
										{text}
									</span>
									{i !== list.length - 1 && (
										<span aria-hidden="true">&bull;</span>
									)}
								</li>
							);
						})}
					</ul>
				)}
				{children}
			</div>
		);
	}

	private mergeSecondaryName(givenName: any, middleName: any) {
		if (middleName) {
			return givenName + ' ' + middleName;
		}
		return givenName;
	}
}
